import { useState } from "react";
import { Link } from "react-router";
import type { PlanSummary } from "@edu/shared";
import * as api from "../../lib/api.ts";
import { pendingMeetingsText } from "../../lib/plurals.ts";
import { RowDisclosure } from "../../lib/RowDisclosure.tsx";
import { agoLabel, leftLabel } from "../../lib/when.ts";
import { planBriefText } from "./plan-brief.ts";

/**
 * Строка списка заготовок.
 *
 * Отдельным файлом от экрана по двум причинам. Формальная: с развёрткой строки
 * и тремя сценариями удаления `PlansPage.tsx` перестал влезать в 400 строк.
 * Существенная: у строки есть собственное состояние (переименование, шаг
 * подтверждения, счётчик неготовых уроков), и держать его в экране значит
 * держать его на каждую строку списка сразу.
 *
 * ⚠️ Действие в строке ровно одно, «Открыть». До переработки их было шесть
 * (Открыть, Уроки·N, Переименовать, Дублировать, В корзину плюс два разных шага
 * подтверждения), и ряд из шести кнопок читался дольше, чем название заготовки
 * рядом с ним. Всё редкое уехало в развёртку строки: правило 6 общего
 * контракта. Открывает её шеврон в своём столбце, а не кнопка «…» в ячейке
 * действий: с 14.09.2026 раскрытие детали строки это одна механика на всё
 * приложение (`lib/RowDisclosure.tsx`).
 *
 * ⚠️ **Ниже порога колонок шесть слотов превращаются в две строки** (Р3
 * `docs/phone-design-decisions.md`, включено `data-rows="panel"` в
 * `PlansPage.tsx`):
 *
 *     [>] Название заготовки                 3 дня назад
 *         12 слайдов · 3 урока                 [Открыть]
 *
 * Состав разметки при этом почти не меняется, а порядок слотов не меняется
 * вовсе: колонку и строку каждому слоту назначает CSS явно, а что спрятано и
 * почему, сказано у самого слота ниже. Прибавляется ровно один узел, сводка
 * («12 слайдов · 3 урока»), и существует он только на узкой строке: разбор у
 * него же. Ветки по ширине в этом файле нет ни одной и быть не должно: второй
 * источник правды о раскладке первым делом разойдётся с первым.
 */
export function PlanRow(props: {
  plan: PlanSummary;
  busy: boolean;
  /** Один `Date.now()` на отрисовку списка, а не на строку. */
  now: number;
  run: (fn: () => Promise<void>) => void;
}) {
  const p = props.plan;
  const [open, setOpen] = useState(false);
  const [renaming, setRenaming] = useState<string | null>(null);
  /** Подтверждение своё, а не `confirm()`: нативный диалог блокирует поток. */
  const [asking, setAsking] = useState(false);
  /**
   * Сколько занятий ещё не взяли материал этой заготовки.
   *
   * ⚠️ Второй вопрос поверх обычного подтверждения, и он не лишний. Такое
   * занятие возьмёт материал при первом открытии доски; если заготовку стереть,
   * оно откроется пустым. Пометка в корзину сама по себе не страшна, но через 30
   * суток корзину чистит уборка, и к тому моменту человек про удаление забыл.
   *
   * ⚠️ Считает сервер, и считает ЗАНЯТИЯМИ: занятие группы это N уроков на одной
   * доске, и вопрос про него один (`plan-pending-rooms.ts`).
   */
  const [pending, setPending] = useState<number | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const updated = agoLabel(p.updatedAt, props.now);

  /**
   * Удаление в один шаг, пока сервер не спросил. Отказ `plan_has_pending_lessons`
   * это не ошибка, а вопрос, поэтому он не уходит в общую строку ошибок.
   */
  const trash = (mode?: "materialize" | "trash") => {
    setErr(null);
    props.run(async () => {
      try {
        await api.trashPlan(p.id, mode);
        setPending(null);
        setAsking(false);
      } catch (e) {
        if (e instanceof api.ApiError && e.code === "plan_has_pending_lessons") {
          const count = e.details["count"];
          setPending(typeof count === "number" ? count : 1);
          return;
        }
        if (e instanceof api.ApiError && e.code === "plan_materialize_failed") {
          setErr("Часть занятий не получила материал, заготовка не удалена. Попробуйте ещё раз");
          return;
        }
        throw e;
      }
    });
  };

  const saveRename = () => {
    if (renaming === null) return;
    const next = renaming.trim();
    if (next) props.run(async () => void (await api.renamePlan(p.id, next)));
    setRenaming(null);
  };

  return (
    <li className="row">
      {/* Шеврон первым столбцом, а не кнопка «…» в ячейке действий: раскрытие
          детали строки это одна механика на всё приложение, см. шапку
          `lib/RowDisclosure.tsx` */}
      <RowDisclosure
        open={open}
        action="Ещё действия"
        subject={p.title}
        onToggle={() => setOpen(!open)}
      />

      {/* Переименование правит название ТАМ ЖЕ, где оно написано: раньше оно
          разворачивало карточку в форму, и название на секунду исчезало из
          строки вместе с колонками соседей */}
      <span className="cell row__name">
        {renaming === null ? (
          p.title
        ) : (
          <input
            className="fld"
            value={renaming}
            autoFocus
            maxLength={200}
            aria-label="Новое название"
            onChange={(e) => setRenaming(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") setRenaming(null);
              if (e.key === "Enter") saveRename();
            }}
          />
        )}
      </span>

      {/*
        Число без слова: подпись колонки стоит в шапке, а «12 слайдов» в
        числовой колонке ломает столбик, ради которого колонка и заведена.

        ⚠️ На узкой строке слот прячется, и прячет его общее правило по
        `data-label` (метки `data-narrow` у него нет). Довод Р3 дословный:
        **прячем то, у чего есть второй адрес, и называем этот адрес.** Адрес у
        числа слайдов это сам экран заготовки, где слайды и лежат. Само число
        при этом со строки не исчезает, оно уезжает в сводку второй строкой
        («12 слайдов · 3 урока») вместе с числом уроков.
      */}
      <span className="cell row__num" data-label="Слайдов">
        {p.slideCount}
      </span>

      {/*
        Ноль и «не считали» здесь разные вещи, см. `lessonCount` в схеме: `null`
        означает, что сервер уроки в этом ответе не считал (старый ответ), и
        притворяться нулём он не должен. Ноль это факт, его показываем цифрой, а
        ссылкой число становится только когда вести есть куда.

        ⚠️ На узкой строке слот прячется тем же общим правилом и по тому же
        доводу Р3, что и слайды. Второй адрес у него это кнопка «Уроки · N» на
        экране заготовки (`PlanBoard.tsx`), то есть ровно то место, куда ведёт
        ссылка из этой ячейки.
      */}
      {p.lessonCount === null ? (
        <span className="cell row__void" data-label="Уроков" title="В этом ответе не считали">
          не считали
        </span>
      ) : (
        <span className="cell row__num" data-label="Уроков">
          {p.lessonCount === 0 ? (
            0
          ) : (
            <Link to={`/plans/${p.id}/lessons`} title="Уроки по этой заготовке">
              {p.lessonCount}
            </Link>
          )}
        </span>
      )}

      {/*
        ⚠️ `data-narrow="keep"`: на узкой строке этот слот остаётся при имени, в
        правом конце первой строки. Время правки тут не украшение, а ключ
        порядка: список отсортирован сервером по нему же (`repo/plans.ts`,
        `orderBy("updated_at", "desc")`). Спрятав его, мы оставили бы список,
        порядок которого не объяснён ничем видимым. Тот же довод оставил дату у
        строки урока.
      */}
      <span
        className="cell row__when"
        data-label="Правка"
        data-narrow="keep"
        data-tone={updated.tone}
        title={updated.title}
      >
        {updated.text}
      </span>

      {/*
        Сводка: два спрятанных числа одной подписью под именем. Слот существует
        ТОЛЬКО ниже порога колонок (`data-wide="none"`), на широкой раскладке
        обе величины стоят своими колонками, и подпись повторяла бы их.

        ⚠️ **Правилу Р3 это не противоречит, хотя узкая строка тут ДОБАВЛЯЕТ
        величину, а не прячет.** У извода `panel` вторая строка есть ВСЕГДА:
        ступень `--h-row-two` плюс ячейка действий, стоящая в её правом конце.
        То есть левая половина второй строки иначе осталась бы пустой, и подпись
        не стоит ни пикселя высоты, место уже оплачено. Р3 говорит, чем платить
        за оставленную величину; здесь платить нечем.

        Порядок в разметке на раскладку не влияет (клетки назначает CSS), слот
        стоит здесь ради чтения файла: сверху вниз он идёт там же, где читается
        в строке.
      */}
      <span className="cell" data-narrow="sub" data-wide="none">
        {planBriefText(p.slideCount, p.lessonCount)}
      </span>

      <span className="cell row__act">
        <Link className="btn btn--sm" to={`/plans/${p.id}`}>
          Открыть
        </Link>
      </span>

      {open && (
        <div className="row__more">
          {err !== null && <p className="err">{err}</p>}

          {pending !== null && <p className="err">{pendingMeetingsText(pending)}</p>}

          {renaming !== null ? (
            <div className="acts">
              {/* Акцента у «Сохранить» нет намеренно: строк в списке много, и
                  одинаковых акцентов на экране стало бы столько же, сколько
                  открытых переименований. Главное действие экрана это «Создать»
                  в форме сверху (правило 4 системы кнопок) */}
              <button
                className="btn"
                disabled={props.busy || renaming.trim() === ""}
                onClick={saveRename}
              >
                Сохранить
              </button>
              <button
                className="btn btn--quiet"
                disabled={props.busy}
                onClick={() => setRenaming(null)}
              >
                Отмена
              </button>
            </div>
          ) : (
            <div className="acts">
              <button className="btn" disabled={props.busy} onClick={() => setRenaming(p.title)}>
                Переименовать
              </button>
              {/* Дублирование это основной способ сделать вариант урока для
                  другого уровня, поэтому оно первым в развёртке, сразу за
                  переименованием: на виду в строке ему места нет, там живёт
                  единственное главное действие */}
              <button
                className="btn"
                disabled={props.busy}
                onClick={() => props.run(async () => void (await api.duplicatePlan(p.id)))}
              >
                Дублировать
              </button>
              {pending !== null ? (
                /*
                  Три кнопки подряд, две из них необратимы, и до расстановки
                  весов отличить их можно было только чтением. Веса тут
                  расставлены так:

                  «Раздать материал и удалить» это заливка (`is-confirm`), потому
                  что это исход, который мы предлагаем: занятия, ещё не взявшие
                  материал, получают его и открываются не пустыми. Он необратим,
                  поэтому красный, а не синий.

                  «Удалить вместе с занятиями» тоже необратимо, но обводкой: два
                  одинаково выделенных необратимых исхода не подсказывают ничего,
                  и выбор снова сводится к чтению. Заливка ровно одна, у того,
                  который мы советуем.

                  Синего здесь нет вовсе: экран спрашивает про необратимое, и
                  акцент рядом с красным читался бы как «безопасный выход»
                  (правило 3).
                */
                <>
                  <button
                    className="btn btn--danger is-confirm"
                    disabled={props.busy}
                    onClick={() => trash("materialize")}
                  >
                    Раздать материал и удалить
                  </button>
                  <button
                    className="btn btn--danger"
                    disabled={props.busy}
                    onClick={() => trash("trash")}
                  >
                    Удалить вместе с занятиями
                  </button>
                  <button
                    className="btn btn--quiet"
                    disabled={props.busy}
                    onClick={() => {
                      setPending(null);
                      setAsking(false);
                    }}
                  >
                    Отмена
                  </button>
                </>
              ) : asking ? (
                <>
                  {/* Шаг подтверждения: следующее нажатие уже уносит заготовку,
                      поэтому заливка, а не обводка */}
                  <button
                    className="btn btn--danger is-confirm"
                    disabled={props.busy}
                    onClick={() => trash()}
                  >
                    Точно в корзину
                  </button>
                  <button
                    className="btn btn--quiet"
                    disabled={props.busy}
                    onClick={() => setAsking(false)}
                  >
                    Отмена
                  </button>
                </>
              ) : (
                /* Удаление пока только предлагается, отсюда обводка: заливку
                   получает шаг подтверждения выше */
                <button
                  className="btn btn--danger"
                  disabled={props.busy}
                  onClick={() => setAsking(true)}
                >
                  В корзину
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </li>
  );
}

/**
 * Строка корзины заготовок.
 *
 * Колонки те же (`list--plans`), а не свой узкий список на две колонки: корзина
 * стоит прямо под списком заготовок, и второй шаблон колонок под ней означал бы
 * два разъехавшихся столбика на одном экране. Своего шаблона в styles.css у
 * корзины заготовок нет, и заводить его ради четырёх строк незачем.
 *
 * Отличие от живой строки ровно в двух слотах: вместо времени правки стоит срок
 * автоматической уборки, а единственное действие это возврат из корзины.
 *
 * ⚠️ Столбец шеврона занят пустым слотом, а не пропущен: раскрывать в корзине
 * нечего, но колонки у списка общие с живыми заготовками, и пропущенный слот
 * сдвинул бы всю строку на один столбец влево.
 *
 * ⚠️ **Ниже порога колонок строка та же, что у живой заготовки** (`panel`,
 * разбор в шапке файла и у `<ul>` в `PlansPage.tsx`):
 *
 *     [ ] Название заготовки                 через 3 дня
 *         12 слайдов · 3 урока             [Восстановить]
 *
 * Значение метки у корзины общее с живым списком не только по необходимости
 * (разрез один, а замок разрезов требует у него ровно одну метку), но и по
 * существу: строка корзины это тем более пульт, действие в ней ровно одно и
 * второго адреса у него нет. Ветки по ширине здесь, как и выше, нет ни одной.
 *
 * ⚠️ **Что уезжает в сводку, тут никуда больше не ведёт.** У живой строки числа
 * прячутся доводом Р3 «есть второй адрес» (экран заготовки и его кнопка
 * «Уроки · N»), а у удалённой ни того, ни другого нет: она не открывается, и
 * ссылки на уроки в ней нет намеренно. Поэтому сводка в корзине это
 * единственное место, где число слайдов и число уроков вообще показаны. То же и
 * со сроком уборки в правом конце: он считается только здесь.
 */
export function PlanTrashRow(props: {
  plan: PlanSummary;
  busy: boolean;
  now: number;
  run: (fn: () => Promise<void>) => void;
}) {
  const p = props.plan;
  // Считается от даты удаления плюс срок хранения: «удалится через 3 дня»
  // отвечает на вопрос, который тут задают, а дата удаления не отвечает
  const left = p.deletedAt === null ? null : leftLabel(p.deletedAt, 30, props.now);

  return (
    <li className="row" data-dim="true">
      <span />
      <span className="cell row__name">{p.title}</span>
      {/*
        Оба числа на узкой строке прячутся общим правилом по `data-label` и
        приезжают сводкой ниже.

        ⚠️ Второго адреса у них тут, в отличие от живой строки, НЕТ ни одного, и
        оба раза намеренно: удалённая заготовка не открывается вовсе (действие в
        строке одно, «Восстановить»), а ссылки на её уроки нет по доводу
        соседнего комментария ниже. То есть в корзине сводка это не добавление
        сверх Р3, а прямое его исполнение: единственное место, где эти числа
        вообще видны.
      */}
      <span className="cell row__num" data-label="Слайдов">
        {p.slideCount}
      </span>
      {/* Ссылки на уроки у удалённой заготовки нет намеренно: строку в корзине
          читают ради одного решения, вернуть её или нет */}
      {p.lessonCount === null ? (
        <span className="cell row__void" data-label="Уроков" title="В этом ответе не считали">
          не считали
        </span>
      ) : (
        <span className="cell row__num" data-label="Уроков">
          {p.lessonCount}
        </span>
      )}
      {/*
        ⚠️ `data-narrow="keep"`: на узкой строке слот остаётся, и довод у него
        НЕ тот, что у времени правки живой строки. Порядок тут ни при чём:
        корзина приезжает тем же запросом, что и живой список, одним массивом по
        `updated_at` (`repo/plans.ts`, `listPlans`), и `trash` это его второй
        фильтр. Довод в другом: **второго адреса у этой величины нет вовсе.**
        Сколько осталось ЭТОЙ заготовке до стирания, не написано больше нигде: в
        шапке секции стоит общее правило («хранится 30 суток с удаления»), а
        остаток считает только эта строка. По Р3 такая величина в строке и
        остаётся, ровно как просрочка домашки у списка учеников.

        ⚠️ Метка стоит у ОБОИХ видов слота, и у срока, и у «срок неизвестен».
        Это одна колонка в двух видах, а не два разных слота: исчезни она на
        строке без даты удаления, строка без правого конца рядом со строкой с
        ним читалась бы как другая форма строки.
      */}
      {left === null ? (
        <span className="cell row__void" data-label="Уборка" data-narrow="keep">
          срок неизвестен
        </span>
      ) : (
        <span
          className="cell row__when"
          data-label="Уборка"
          data-narrow="keep"
          data-tone={left.tone}
          title={left.title}
        >
          {left.text}
        </span>
      )}
      {/* Сводка на месте спрятанных чисел, разбор у того же слота живой строки:
          вторая строка у пульта есть всегда, и её левая половина иначе пустует */}
      <span className="cell" data-narrow="sub" data-wide="none">
        {planBriefText(p.slideCount, p.lessonCount)}
      </span>
      <span className="cell row__act">
        <button
          className="btn btn--sm"
          disabled={props.busy}
          onClick={() => props.run(async () => void (await api.restorePlan(p.id)))}
        >
          Восстановить
        </button>
      </span>
    </li>
  );
}
